import { Link } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import { Check, X, LayoutList } from 'lucide-react';
import ShareIcon from '../ui/ShareIcon';
import { EventCard } from '../EventCard';
import {
  GLASS_SURFACES,
  CARD_SOLID_SURFACE_CLASS,
  groupMembersListSurfaceClass,
} from '../../config/glassSurfaces';
import Avatar from '../Avatar';
import Button from '../ui/Button';
import TextInput from '../ui/TextInput';
import LineupViewToggle from '../lineup/LineupViewToggle';

/** rename_group's limit; the field stops at it instead of counting past it. */
const GROUP_NAME_MAX = 40;

/**
 * Group metadata + manager controls — shown in the info modal (formerly inline card above chat).
 */
/**
 * The group's name and its editor, at the top of the group page beside the back button (operator, 2026-09-23:
 * "above everything"). A manager edits it in place; an auto-named group opens here, empty and focused.
 */
export function GroupNameHeading({
  displayGroupName,
  isManager,
  isEditingGroupName,
  groupNameDraft,
  onGroupNameDraftChange,
  groupRenameError,
  onGroupRenameErrorClear,
  savingGroupName,
  onStartEditingGroupName,
  onCancelEditingGroupName,
  onSaveGroupName,
  namePlaceholder,
}) {
  return (
    <div className="min-w-0 flex-1">
      {isManager && isEditingGroupName ? (
        <div className="w-full space-y-1">
          {/* The standard field, as everywhere else (Sam, 2026-09-28): TextInput unstyled, 44 px square buttons like
              the chat composer's Send, one row. The pulsing invite-nudge glow belonged to the Invite button - on an
              input it replaced the focus ring - and text-lg/font-bold made this the one bold field in the app. */}
          <div className="flex items-center gap-2">
            <TextInput
              autoFocus
              type="text"
              aria-label="Group name"
              placeholder={namePlaceholder}
              value={groupNameDraft}
              maxLength={GROUP_NAME_MAX}
              enterKeyHint="done"
              autoCapitalize="words"
              autoComplete="off"
              onChange={(e) => {
                onGroupRenameErrorClear();
                onGroupNameDraftChange(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSaveGroupName();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEditingGroupName();
                }
              }}
              invalid={Boolean(groupRenameError)}
              className="min-w-0 flex-1"
              aria-invalid={Boolean(groupRenameError)}
            />
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={onSaveGroupName}
              disabled={savingGroupName || (groupNameDraft || '').trim().length < 1}
              aria-label="Save group name"
              className="h-[2.75rem] min-h-[2.75rem] w-[2.75rem] flex-shrink-0 !p-0"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onCancelEditingGroupName}
              disabled={savingGroupName}
              aria-label="Cancel editing group name"
              className="h-[2.75rem] min-h-[2.75rem] w-[2.75rem] flex-shrink-0 !p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {groupRenameError ? (
            <p className="text-sm text-danger" role="alert">
              {groupRenameError}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="glass-title-emphasis truncate text-xl font-bold text-gray-900 dark:text-dark-text sm:text-2xl">
            {displayGroupName}
          </h1>
          {isManager && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onStartEditingGroupName}
              className="shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-dark-muted dark:hover:bg-dark-border"
              aria-label="Edit group name"
            >
              <FiEdit2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function GroupInfoPanel({
  eventForCard,
  isArchived,
  isManager,
  isMember,
  isPrivateGroup,
  onSetAccessType,
  hasLineup,
  onOpenLineup,
  setsGlow = '',
  shared,
  onCopyShareLink,
  pendingJoinRequests,
  onApproveJoinRequest,
  onDenyJoinRequest,
  approveNewMembers = false,
  onSetApproveNewMembers,
  onResetInviteLink,
  highlightInvite = false,
}) {
  const showShareButton = !isArchived && isMember;

  return (
    <div className="space-y-6">
      <div
        className={`event-card-surface overflow-hidden rounded-[18px] p-4 sm:p-6 ${GLASS_SURFACES.cards ? '' : CARD_SOLID_SURFACE_CLASS}`}
      >
        {eventForCard && (
          <div className="mb-4">
            <Link
              to={`/events/${eventForCard.event_id}`}
              className="block rounded-2xl no-underline text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <EventCard
                event={eventForCard}
                readOnly
                variant="hero"
                cornerLabel={isArchived ? 'Past' : null}
              />
            </Link>
          </div>
        )}

        {isManager && (
          <div className="mb-3 flex min-h-[36px] flex-nowrap items-center gap-3">
            {/* The app's segmented pill, not two buttons swapping variants: those changed size and both dimmed while
                saving, the jitter Sam saw (2026-10-21). A tap during a save is ignored by the hook. */}
            <LineupViewToggle
              view={isPrivateGroup ? 'private' : 'public'}
              onChange={(v) => onSetAccessType(v === 'private')}
              options={[
                { value: 'public', label: 'Public' },
                { value: 'private', label: 'Private' },
              ]}
              ariaLabel="Group visibility"
              className="!m-0 w-[11.5rem] flex-shrink-0"
            />
            <span className="min-w-0 truncate text-xs text-gray-500 dark:text-dark-muted">
              {isPrivateGroup ? 'Ask to join' : 'Anyone can join'}
            </span>
          </div>
        )}

        {/* Two buttons (operator, 2026-09-23): Invite and Sets. Members are listed under the card, not a page away.
            A new group's Invite glows until it is used - the prompt is the button itself, not a second one. */}
        {(showShareButton || hasLineup) && (
          <div
            className={
              showShareButton && hasLineup
                ? 'grid min-w-0 grid-cols-2 items-stretch gap-2'
                : 'grid min-w-0 grid-cols-1'
            }
          >
            {showShareButton && (
              <Button
                type="button"
                size="sm"
                variant="primary"
                onClick={onCopyShareLink}
                data-moment="invite"
                aria-label={shared ? 'Link copied' : 'Invite'}
                className={`min-h-[48px] min-w-0 w-full justify-center${highlightInvite ? ' invite-nudge' : ''}`}
                leftIcon={
                  shared ? (
                    <Check className="h-4 w-4 shrink-0" aria-hidden />
                  ) : (
                    <ShareIcon className="h-4 w-4 shrink-0" aria-hidden />
                  )
                }
              >
                <span className="truncate">{shared ? 'Link copied' : 'Invite'}</span>
              </Button>
            )}
            {hasLineup && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={onOpenLineup}
                aria-label="Sets"
                className={`${setsGlow} min-h-[48px] min-w-0 w-full justify-center`}
                leftIcon={<LayoutList className="h-4 w-4 shrink-0" aria-hidden />}
              >
                <span className="truncate">Sets</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* The link's two manager controls, WhatsApp's "Approve new members" and "Reset link" (migration 128). */}
      {isManager && !isArchived && (onSetApproveNewMembers || onResetInviteLink) && (
        <section
          className={`event-card-surface rounded-[18px] p-4 sm:p-6 ${GLASS_SURFACES.cards ? '' : CARD_SOLID_SURFACE_CLASS}`}
          aria-label="Invite link settings"
        >
          <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-dark-text">
            Invite link
          </h2>
          {onSetApproveNewMembers && (
            <label className="mb-3 flex cursor-pointer items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 dark:text-dark-text">
                  Approve new members
                </span>
                <span className="block text-xs text-gray-500 dark:text-dark-muted">
                  Friends who open the link need a manager&apos;s OK to join.
                </span>
              </span>
              <input
                type="checkbox"
                role="switch"
                className="mt-1 h-5 w-5 shrink-0 accent-[#9a49d2]"
                checked={approveNewMembers}
                onChange={(e) => onSetApproveNewMembers(e.target.checked)}
              />
            </label>
          )}
          {onResetInviteLink && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500 dark:text-dark-muted">
                Stops the current link working. Share the new one.
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={onResetInviteLink}
                className="shrink-0"
              >
                Reset link
              </Button>
            </div>
          )}
        </section>
      )}

      {/* Same card, list surface and buttons as the rest of the page (operator, 2026-10-04: the old block
          was a white slab with its own border and button styles). */}
      {isManager && pendingJoinRequests.length > 0 && (
        <section
          className={`event-card-surface overflow-hidden rounded-[18px] p-4 sm:p-6 ${GLASS_SURFACES.cards ? '' : CARD_SOLID_SURFACE_CLASS}`}
        >
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-muted">
            Requests · {pendingJoinRequests.length}
          </h2>
          <ul className={groupMembersListSurfaceClass()}>
            {pendingJoinRequests.map((req) => {
              const name = req.profiles?.display_name || 'Someone';
              return (
                <li key={req.id} className="flex items-center gap-3 px-2 py-2">
                  <div className="shrink-0">
                    <Avatar
                      displayName={name}
                      profilePhotoUrl={req.profiles?.profile_photo_url}
                      size="sm"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-900 dark:text-dark-text">
                      {name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-muted">Asked to join</div>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onDenyJoinRequest(req)}
                    className="min-h-[44px] shrink-0"
                    aria-label={`Deny ${name}`}
                  >
                    Deny
                  </Button>
                  <Button
                    type="button"
                    onClick={() => onApproveJoinRequest(req)}
                    className="min-h-[44px] shrink-0"
                    aria-label={`Approve ${name}`}
                  >
                    Approve
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
